import { CSSProperties } from 'react';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { DateTimePicker, TimePicker } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { AgColor } from '../../../Styles/AgColor';
import AgTypography from '../../../Styles/AgTypography';

import { defaultLocale } from './locales';

export interface AgDatePickerProps {
  id?: string;
  label?: string;
  disabled?: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange?: (date: dayjs.Dayjs | null) => void;
  defaultValue?: dayjs.Dayjs;
  value?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  minDate?: dayjs.Dayjs;
  onOpen?: () => void;
  style?: CSSProperties;
  captionStyle?: CSSProperties;
  error?: boolean;
  required?: boolean;
  optionalText?: boolean;
  caption?: string | string[];
  onClose?: () => void;
  hideLabel?: boolean;
  withTime?: boolean; // if true; show the DateTimePicker instead
  clearable?: boolean;
  timeOnly?: boolean;
}

const AgDatePicker = (
  {
    id,
    label,
    disabled,
    onChange,
    defaultValue,
    value,
    maxDate,
    minDate,
    onOpen,
    style,
    captionStyle,
    error,
    required,
    caption,
    optionalText,
    onClose,
    hideLabel,
    withTime = false,
    clearable = true,
    timeOnly = false,
  }: AgDatePickerProps
) => {
  const messages = useIntl().formatMessage;

  // Create a custom locale to modify the months based on our localization.
  // The name you put dayjs.locale will be used on the adapterLocale.
  dayjs.locale({ 
    ...defaultLocale,
    name: 'custom-locale',  
    months: [
      messages({ id: 'MONTH_JANUARY' }),
      messages({ id: 'MONTH_FEBRUARY' }),
      messages({ id: 'MONTH_MARCH' }),
      messages({ id: 'MONTH_APRIL' }),
      messages({ id: 'MONTH_MAY' }),
      messages({ id: 'MONTH_JUNE' }),
      messages({ id: 'MONTH_JULY' }),
      messages({ id: 'MONTH_AUGUST' }),
      messages({ id: 'MONTH_SEPTEMBER' }),
      messages({ id: 'MONTH_OCTOBER' }),
      messages({ id: 'MONTH_NOVEMBER' }),
      messages({ id: 'MONTH_DECEMBER' }),
    ]
  });

  const commonProps = {
    disabled: disabled,
    onChange: onChange,
    onClose: onClose,
    defaultValue: defaultValue,
    value: value,
    maxDate: maxDate,
    minDate: minDate,
    maxTime: maxDate,
    minTime: minDate,
    onOpen: onOpen,
    sx: {
      padding: '6px 0',
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        border: disabled ? 'none' : '1px solid',
        borderColor: disabled
          ? AgColor.grey3
          : error
            ? AgColor.errorMain
            : AgColor.link1
      },
      '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        border: disabled ? 'none' : '1px solid',
        borderColor: disabled
          ? AgColor.grey3
          : error
            ? AgColor.errorMain
            : `${AgColor.grey4} !important`
      },
      '& .MuiOutlinedInput-input': {
        padding: '6px 0 6px 12px',
        lineHeight: '26px',
        height: '26px',
        fontSize: '16px',
        fontFamily: 'Inter, Sans-Serif',
        fontWeight: 400,
        letterSpacing: '0',
        textTransform: 'none',
        color: AgColor.grey9
      },
      '& .MuiInputBase-root': {
        paddingRight: '12px',
        height: '38px',
        backgroundColor: disabled ? AgColor.grey3 : AgColor.grey1
      },
      '& .MuiButtonBase-root': {
        padding: 0,
        margin: 0
      },
      '& .MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
        color: disabled ? AgColor.grey6 : AgColor.grey9
      },
      '& .clearButton': {
        padding: '3px',
        marginRight: '5px',
      }
    },
    slots: {
      switchViewIcon: ExpandMoreIcon
    },
    slotProps: {
      field: { clearable, onClear: () => onChange && onChange(null) },
      day: {
        sx: {
          color: AgColor.grey8,
          fontSize: '14px',
          lineHeight: '26px',
          fontFamily: 'inter, Sans-Serif',
          fontWeight: 400
        }
      },
      popper: {
        sx: {
          paddingTop: '4px',
          '& .MuiPaper-root': {
            borderRadius: '8px',
            border: `0.5px solid ${AgColor.grey3}`
          },
          '& .Mui-selected': {
            backgroundColor: `${AgColor.secondaryMain} !important`
          },
          '& button:hover': {
            backgroundColor: AgColor.secondaryLight2
          },
          '& .MuiPickersPopper-paper': {
            boxShadow:
              'rgb(255, 255, 255) 0px 0px 0px 0px, ' +
              '#0000000D 0px 0px 0px 1px, ' +
              'rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, ' +
              '#0000000D 0px 4px 6px -2px',
          },
          '& .MuiPickersYear-yearButton': {
            color: AgColor.grey8,
            fontSize: '16px',
            lineHeight: '26px',
            fontWeight: 400,
            fontFamily: 'inter, Sans-Serif'
          },
          '& .MuiPickersCalendarHeader-label': {
            color: AgColor.grey8,
            fontSize: '16px',
            lineHeight: '26px',
            fontWeight: 600,
            fontFamily: 'inter, Sans-Serif'
          },
          '& .MuiPickersDay-today': {
            border: 'none !important',
            backgroundColor: AgColor.grey0
          },
          '& .MuiDayCalendar-weekDayLabel': {
            color: AgColor.grey5,
            fontSize: '12px',
            lineHeight: '18px',
            fontWeight: 400,
            fontFamily: 'inter, Sans-Serif'
          }
        }
      }
    }
  };

  return (
    <AgTypography style={{ ...style }}>
      {!hideLabel && <AgTypography
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          height: '20px',
          alignItems: 'center'
        }}
      >
        <label
          htmlFor={id}
          style={{
            fontSize: '12px',
            lineHeight: '16px',
            fontFamily: 'Roboto, sans-serif',
            color: AgColor.grey9,
            fontWeight: 500
          }}>
          {label}
        </label>
        {
          !required && optionalText &&
          <AgTypography
            contentStyle={'AP.Assistive.Text.Regular'}
            message={messages({ id: 'STORYBOOK_OPTIONAL' })}
          />
        }
      </AgTypography>}

      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale='custom-locale'
      >
        {withTime ? (
          <DateTimePicker timeSteps={{ minutes: 1 }} {...commonProps} />
        ) : timeOnly ? (
          <TimePicker views={['hours', 'minutes']} format='HH:mm' ampm={false} {...commonProps} />
        ) : (
          <DatePicker {...commonProps} />
        )}
      </LocalizationProvider>
      <AgTypography
        contentStyle={'AP.Assistive.Text.Regular'}
        id={id}
        style={{
          marginLeft: '0',
          color: error ? AgColor.errorMain : AgColor.grey7,
          ...captionStyle
        }}>
        {caption}
      </AgTypography>
    </AgTypography>
  );
};

export default AgDatePicker;